import Calendar from '@fullcalendar/react';
import listPlugin from '@fullcalendar/list';
import dayGridPlugin from '@fullcalendar/daygrid';
import { useEffect, startTransition } from 'react';
import timeGridPlugin from '@fullcalendar/timegrid';
import timelinePlugin from '@fullcalendar/timeline';
import interactionPlugin from '@fullcalendar/interaction';
import { useBoolean, useSetState } from 'minimal-shared/hooks';

import Card from '@mui/material/Card';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import { RoundedDialog } from '../../Components';

import { fDate, fIsAfter, fIsBetween } from '../../../../utils/format-time';

import { CALENDAR_COLOR_OPTIONS } from '../_calendar';

import { CalendarRoot } from '../styles';
import { useEvent } from '../hooks/use-event';
import { CalendarForm } from '../calendar-form';
import { useCalendar } from '../hooks/use-calendar';
import { CalendarToolbar } from '../calendar-toolbar';
import { CalendarFilters } from '../calendar-filters';
import { CalendarFiltersResult } from '../calendar-filters-result';
import { DialogContent } from '@mui/material';
import { amber, green, purple, red, blue, grey } from '@mui/material/colors';
import moment from 'moment';
import { isEmpty } from 'lodash';

import axios from 'axios';
import { useState } from 'react';

import StateManager from '../../StateManager';

const colors = {
  process_step: amber[500],
  routine: blue[900],
  form_entry: green[500],
  asset: blue[500],
  workflow: purple[500],
  audit: blue[500],
  project: amber[500],
};

// ----------------------------------------------------------------------

export function CalendarView({ forHub }) {
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [completed, setCompleted] = useState(false);
  const [typeFilter, setTypeFilter] = useState([]);
  const openFilters = useBoolean();

  const filters = useSetState({ colors: [], startDate: null, endDate: null, typeFilter });
  const currentFilters = { ...filters.state, typeFilter };

  const dateError = fIsAfter(currentFilters.startDate, currentFilters.endDate);

  const {
    calendarRef,
    /********/
    view,
    date,
    /********/
    onDatePrev,
    onDateNext,
    onDateToday,
    onDropEvent,
    onChangeView,
    onSelectRange,
    onResizeEvent,
    onInitialView,
    /********/
    openForm,
    onCloseForm,
    /********/
    selectEventId,
    selectedRange,

    /********/
  } = useCalendar();

  const currentEvent = useEvent(events, selectEventId, selectedRange, openForm);

  useEffect(() => {
    const timeUnitsMap = {
      dayGridMonth: 'month',
      timeGridWeek: 'week',
      timeGridDay: 'day',
      listWeek: 'week',
    };

    if (!date) {
      console.log('no date!!!!!');
      return;
    }
    if (!view) {
      console.log('no view!!!!!');
      return;
    }

    const start = moment(date).startOf(timeUnitsMap[view]);
    const end = moment(date).endOf(timeUnitsMap[view]);

    setEventsLoading(true);

    axios
      .post('/calendar/getCalendarEvents', { withCompleted: completed, start, end })
      .then(({ data }) => {
        const now = moment();
        const res = data.map((task) => ({
          ...task,
          color: moment(task.end) < now ? red[500] : colors[task.type] ?? grey[500],
          eventDisplay: task.start === task.end ? 'list-item' : 'block',
          display: task.start === task.end ? 'list-item' : 'block',
          allDay: false,
          editable: false,
        }));
        setEvents(res);
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
      })
      .finally(() => {
        setEventsLoading(false);
      });
  }, [date, view, completed]);

  useEffect(() => {
    onInitialView();
  }, [onInitialView]);

  function updateEvent() {
    console.log('updateEvent');
  }

  const canReset = currentFilters.colors.length > 0 || (!!currentFilters.startDate && !!currentFilters.endDate);

  const dataFiltered = applyFilter({
    inputData: events,
    filters: currentFilters,
    dateError,
  });

  const renderResults = () => (
    <CalendarFiltersResult filters={filters} totalResults={dataFiltered.length} sx={{ mb: { xs: 3, md: 5 } }} />
  );

  const flexStyles = {
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'column',
  };

  function updateTask(data) {
    if (!data._id) return;

    const index = events.findIndex((x) => x.id === data._id);

    if (index === -1) return;

    console.log(index);

    const copy = [...events];
    copy[index] = { ...copy[index], title: data.title };
    setEvents(copy);
  }

  function openTask(task) {
    StateManager.selectTask(task.id, (data) => updateTask(data));
  }

  return (
    <>
      <Grid sx={{ ...flexStyles }}>
        {/* <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: { xs: 3, md: 5 },
          }}
        >
          <Typography variant="h4">Calendar</Typography>
          <Button variant="contained" startIcon={<Iconify icon="mingcute:add-line" />} onClick={onOpenForm}>
            New event
          </Button>
        </Box> */}

        {canReset && renderResults()}

        <Card
          sx={{
            ...flexStyles,
            height: '85vh',
          }}
        >
          <CalendarRoot
            sx={{
              ...flexStyles,
              '.fc.fc-media-screen': { flex: '1 1 auto' },
            }}
          >
            <CalendarToolbar
              date={fDate(date)}
              view={view}
              canReset={canReset}
              loading={eventsLoading}
              onNextDate={onDateNext}
              onPrevDate={onDatePrev}
              onToday={onDateToday}
              onCompletedChange={() => setCompleted(!completed)}
              withCompleted={completed}
              onChangeView={(...props) => {
                onChangeView(...props);
              }}
              onOpenFilters={openFilters.onTrue}
              onTypeFilterChange={setTypeFilter}
            />

            <Calendar
              weekends
              editable
              droppable
              //selectable will uncomment when add functionality added
              rerenderDelay={10}
              allDayMaintainDuration
              eventResizableFromStart
              displayEventTime={false}
              ref={calendarRef}
              initialDate={date}
              initialView={view}
              dayMaxEventRows={7}
              events={dataFiltered}
              headerToolbar={false}
              select={onSelectRange}
              eventClick={(arg) => openTask(arg.event)}
              aspectRatio={4}
              eventDrop={(arg) => {
                startTransition(() => {
                  onDropEvent(arg, updateEvent);
                });
              }}
              eventResize={(arg) => {
                startTransition(() => {
                  onResizeEvent(arg, updateEvent);
                });
              }}
              plugins={[listPlugin, dayGridPlugin, timelinePlugin, timeGridPlugin, interactionPlugin]}
              eventTextColor="white"
            />
          </CalendarRoot>
        </Card>
      </Grid>

      <RoundedDialog
        fullWidth
        open={openForm}
        onClose={onCloseForm}
        // transitionDuration={{
        //   enter: theme.transitions.duration.shortest,
        //   exit: theme.transitions.duration.shortest - 80,
        // }}
        // PaperProps={{
        //   sx: {
        //     display: 'flex',
        //     overflow: 'hidden',
        //     flexDirection: 'column',
        //     '& form': {
        //       ...flexStyles,
        //       minHeight: 0,
        //     },
        //   },
        // }}
      >
        <DialogTitle sx={{ minHeight: 76 }}>{openForm && <> {currentEvent?.id ? 'Edit' : 'Add'} event</>}</DialogTitle>
        <DialogContent>
          <CalendarForm currentEvent={currentEvent} colorOptions={CALENDAR_COLOR_OPTIONS} onClose={onCloseForm} />
        </DialogContent>
      </RoundedDialog>

      <CalendarFilters
        events={events}
        filters={filters}
        canReset={canReset}
        dateError={dateError}
        open={openFilters.value}
        onClose={openFilters.onFalse}
        onClickEvent={(arg) => openTask(arg.event)}
        colorOptions={CALENDAR_COLOR_OPTIONS}
      />
    </>
  );
}

// ----------------------------------------------------------------------

function applyFilter({ inputData, filters, dateError }) {
  const { colors, startDate, endDate, typeFilter } = filters;

  const stabilizedThis = inputData.map((el, index) => [el, index]);

  inputData = stabilizedThis.map((el) => el[0]);

  if (colors.length) {
    inputData = inputData.filter((event) => colors.includes(event.color));
  }

  if (!isEmpty(typeFilter)) {
    inputData = inputData.filter((event) => typeFilter.includes(event.type));
  }

  if (!dateError) {
    if (startDate && endDate) {
      inputData = inputData.filter((event) => fIsBetween(event.start, startDate, endDate));
    }
  }

  return inputData;
}
